import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
    runInAction,
} from "mobx";
import type { ChatSession } from "services/AcxAgentService";
import { BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";
import { AcxStore } from "stores/RootStore";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import { AcxAgentService } from "services/AcxAgentService";
import { ServiceError } from "services/BaseService";
import { AuthStore } from "stores/AuthStore";
import SingleAgentChatStore from "./SingleAgentChatStore";
import KboUserAgreementStore from "./KboUserAgreementStore";
import type { ChatNoMessages } from "../Models/Chat";
import { Chat } from "../Models/Chat";
import type { OrganizationKBoStatus } from "../Models/OrganizationKBoStatus";
import type { KboSettings } from "../Models/KboSettings";
import {
    cloneNullableKboSettings,
    hasKboSettingsOverrides,
} from "../Models/KboSettingsHelpers";
import { categorizeChats } from "./ChatCategorization";
import LocalStorage from "stores/LocalStorage";
import {
    type KboFlowReadStateStorage,
    loadKboFlowReadChatIds,
    saveKboFlowReadChatIds,
} from "./KboFlowReadState";
import {
    hasSeenKboUpdate,
    storeKboUpdateSeen,
    KBO_UPDATE_ALWAYS_VISIBLE,
} from "../Update/kboUpdateAnnouncement";

const emptyGuid = "00000000-0000-0000-0000-000000000000";
// A chat object that we create new empty chat stores from
const emptyChat: ChatNoMessages = {
    id: emptyGuid,
    createdOn: "",
    firstName: "KBo",
    lastName: "",
    username: "KBo",
    name: "The Empty Chat - SHOULDNT BE VISIBLE",
};

const emptyKboStatus: OrganizationKBoStatus = {
    organization: {
        isActive: false,
    },
    user: {
        hasViewKboPermission: false,
        hasKboAnalysisSubscription: false,
    },
};

@AcxStore
export default class AgentStore extends BaseStore {
    public static readonly Tasks = {
        RequestAccess: "Request Access to KBo",
    } as const;

    private readonly messageStore: MessageStore;
    private readonly authStore: AuthStore;
    readonly userAgreementStore: KboUserAgreementStore;
    private readonly kboFlowReadStateStorage: KboFlowReadStateStorage;

    private kboFlowReadStateScope?: {
        userId: string;
        organizationId: string;
    };
    private kboFlowReadStateLoad?: Promise<void>;
    private pendingKboFlowReadChatIds = new Set<string>();

    private readonly service = new AcxAgentService();

    @observable isDrawerOpen: boolean = false;
    @observable loadingCurrentChat: boolean = false;
    @observable isEmpty: boolean = true;
    @observable routeBeforeFullscreen: string = "/app/";
    @observable fullscreen: boolean = false;
    @observable isAccessRequested: boolean = false;
    @observable hasResolvedKboStatus: boolean = false;
    @observable isPaywallDialogOpen: boolean = false;
    @observable private kboUpdateAnnouncementActive?: boolean;
    @observable private kboUpdateSparkleCleared: boolean = false;
    @observable.ref defaultSessionSettings: KboSettings | null = null;

    @observable currentChatId: string;
    // The empty chat will always be in our list of chats, its just not visible
    @observable allChats: Map<string, SingleAgentChatStore> = new Map([
        [
            emptyGuid,
            new SingleAgentChatStore({
                chat: Chat.fromChatNoMessage(emptyChat),
                rootStore: this.rootStore,
                agentStore: this,
            }),
        ],
    ]);

    @observable visibleChatIds: string[] = [];
    @observable kboStatus: OrganizationKBoStatus = emptyKboStatus;
    @observable private kboFlowReadChatIds: string[] = [];
    @observable private hasHydratedKboFlowReadState: boolean = false;

    constructor(public rootStore: IRootStore) {
        super("AgentStore");
        makeObservable(this);

        this.messageStore = rootStore.getStore(MessageStore);
        this.authStore = rootStore.getStore(AuthStore);
        this.userAgreementStore = rootStore.getStore(KboUserAgreementStore);
        this.kboFlowReadStateStorage = rootStore
            .getStore(LocalStorage)
            .getLocalStore("KboFlowReadState");
        // Current chat id is empty by default
        this.currentChatId = emptyGuid;

        reaction(
            () => ({
                userId: this.authStore.effectiveUserId,
                organizationId: this.authStore.effectiveOrgId,
            }),
            ({ userId, organizationId }) => {
                this.hydrateKboFlowReadState(userId, organizationId);
            },
            { fireImmediately: true },
        );

        void this.refreshKboStatus();
        void this.refreshDefaultSessionSettings();
    }

    @action
    private hydrateKboFlowReadState(
        userId?: string,
        organizationId?: string,
    ) {
        this.kboFlowReadStateScope = userId && organizationId
            ? { userId, organizationId }
            : undefined;
        this.kboFlowReadChatIds = [];
        this.pendingKboFlowReadChatIds = new Set();
        this.hasHydratedKboFlowReadState = false;

        const scope = this.kboFlowReadStateScope;
        if (!scope) {
            this.kboFlowReadStateLoad = Promise.resolve();
            return;
        }

        this.kboFlowReadStateLoad = (async () => {
            try {
                const storedChatIds = await loadKboFlowReadChatIds(
                    this.kboFlowReadStateStorage,
                    scope.userId,
                    scope.organizationId,
                );
                if (this.kboFlowReadStateScope !== scope) return;

                runInAction(() => {
                    this.kboFlowReadChatIds = [
                        ...new Set([
                            ...storedChatIds,
                            ...this.pendingKboFlowReadChatIds,
                        ]),
                    ];
                    this.hasHydratedKboFlowReadState = true;
                });
            } catch (error) {
                if (this.kboFlowReadStateScope !== scope) return;

                console.error("Failed to load KBo Flow read state:", error);
                runInAction(() => {
                    this.kboFlowReadChatIds = [
                        ...this.pendingKboFlowReadChatIds,
                    ];
                    this.hasHydratedKboFlowReadState = true;
                });
            }

            if (this.pendingKboFlowReadChatIds.size > 0) {
                void this.persistKboFlowReadState(scope);
            }
        })();
    }

    private async persistKboFlowReadState(
        scope = this.kboFlowReadStateScope,
    ) {
        if (!scope || this.kboFlowReadStateScope !== scope) return;

        try {
            await saveKboFlowReadChatIds(
                this.kboFlowReadStateStorage,
                scope.userId,
                scope.organizationId,
                this.kboFlowReadChatIds,
            );
        } catch (error) {
            console.error("Failed to save KBo Flow read state:", error);
        }
    }

    public isKboFlowChatUnread(chat: Chat) {
        return (
            this.hasHydratedKboFlowReadState &&
            chat.isKboFlowResearch &&
            !this.kboFlowReadChatIds.includes(chat.id)
        );
    }

    @action
    markKboFlowChatRead(chat: Chat) {
        if (!chat.isKboFlowResearch || !this.kboFlowReadStateScope) return;
        if (this.kboFlowReadChatIds.includes(chat.id)) return;

        this.pendingKboFlowReadChatIds.add(chat.id);
        this.kboFlowReadChatIds = [...this.kboFlowReadChatIds, chat.id];

        if (this.hasHydratedKboFlowReadState) {
            void this.persistKboFlowReadState();
        } else {
            void this.kboFlowReadStateLoad?.then(() =>
                this.persistKboFlowReadState(),
            );
        }
    }

    @action private setKboStatus(status: OrganizationKBoStatus) {
        this.kboStatus = status;
        this.hasResolvedKboStatus = true;
    }

    @action private setDefaultKboStatus() {
        this.kboStatus = emptyKboStatus;
        this.hasResolvedKboStatus = true;
    }

    public async refreshKboStatus() {
        try {
            const status = await this.service.getOrganizationKboStatus();
            this.setKboStatus(status);
        } catch {
            this.setDefaultKboStatus();
        }
    }

    public async refreshDefaultSessionSettings() {
        if (!this.authStore.isUserUltra()) {
            this.setDefaultSessionSettings(null);
            return;
        }

        try {
            const settings = await this.service.getUserKboSettings();
            this.setDefaultSessionSettings(settings);
        } catch {
            this.setDefaultSessionSettings(null);
        }
    }

    @action
    setDefaultSessionSettings(settings: KboSettings | null | undefined) {
        const normalizedSettings = hasKboSettingsOverrides(settings)
            ? cloneNullableKboSettings(settings)
            : null;

        this.defaultSessionSettings = normalizedSettings;

        const emptyChatStore = this.allChats.get(emptyGuid);
        if (
            this.currentChatId === emptyGuid &&
            emptyChatStore &&
            !emptyChatStore.chat.hasUserMessaged
        ) {
            emptyChatStore.setSessionSettings(normalizedSettings);
        }
    }

    @computed
    get hasViewKboPermission() {
        return (
            this.authStore.canUserView("View KBo") ||
            this.kboStatus.user.hasViewKboPermission
        );
    }

    @computed
    get hasKboAnalysisSubscription() {
        return this.kboStatus.user.hasKboAnalysisSubscription;
    }

    @computed
    get canAccessKboRoutes() {
        return this.hasViewKboPermission || this.hasKboAnalysisSubscription;
    }

    @computed
    get canOpenKboThroughUI() {
        return this.hasViewKboPermission;
    }

    @computed
    get shouldPaywallOnInteract() {
        return this.hasKboAnalysisSubscription && !this.hasViewKboPermission;
    }

    @computed
    get hasReachedKboEmailAnalysisLimit() {
        return this.hasKboAnalysisSubscription && !this.hasViewKboPermission;
    }

    /**
     * Chatting and KBo actions are locked until the user accepts the KBo User
     * Agreement. Users who would hit the paywall keep the paywall instead.
     */
    @computed
    get isKboInteractionLocked() {
        return (
            this.hasViewKboPermission &&
            this.userAgreementStore.requiresAcceptance
        );
    }

    /** Brings up the agreement, opening the KBo panel unless KBo is fullscreen. */
    @action
    showUserAgreement() {
        this.userAgreementStore.expand();
        if (!this.fullscreen) this.openDrawer();
    }

    /**
     * Call before anything that sends to or acts through KBo chat. While the chat
     * is locked it shows the agreement instead and returns false.
     */
    ensureUserAgreementAccepted(): boolean {
        if (!this.isKboInteractionLocked) return true;
        this.showUserAgreement();
        return false;
    }

    /** The server refused a request because the agreement hasn't been accepted. */
    async handleUserAgreementRequired() {
        await this.userAgreementStore.refresh();
        this.showUserAgreement();
    }

    async acceptUserAgreement() {
        const accepted = await this.userAgreementStore.accept();
        if (accepted) this.currentChatStore.showUserAgreementAccepted();
    }

    @action
    openPaywallDialog() {
        this.isPaywallDialogOpen = true;
    }

    @action
    closePaywallDialog() {
        this.isPaywallDialogOpen = false;
    }

    @action private newEmptyChat() {
        this.allChats.set(
            emptyGuid,
            new SingleAgentChatStore({
                chat: Chat.fromChatNoMessage(emptyChat),
                rootStore: this.rootStore,
                agentStore: this,
                sessionSettings: this.defaultSessionSettings,
            }),
        );
        // We can return it with proper type since we literally just set it
        return this.allChats.get(emptyGuid) as SingleAgentChatStore;
    }

    // Computed value of the current chat store based on currentChatId
    @computed
    get currentChatStore() {
        let store: SingleAgentChatStore | undefined;
        if (
            this.currentChatId === emptyGuid ||
            this.currentChatId.trim() === ""
        ) {
            // If the current chat id is the empty guid or is empty string, use the empty chat store
            store = this.allChats.get(emptyGuid);
            if (!store) {
                // Make a new empty chat store if we don't have one anymore
                this.newEmptyChat();
                store = this.allChats.get(emptyGuid);
            }
        } else {
            store = this.allChats.get(this.currentChatId);
            if (store === undefined) {
                store = this.allChats.get(emptyGuid);
                if (!store) {
                    // Make a new empty chat store if we don't have one anymore
                    this.newEmptyChat();
                    store = this.allChats.get(emptyGuid);
                }
            }
        }
        if (store === undefined) {
            console.error(
                "Current chat store is undefined, defaulting to empty",
            );
            store = this.allChats.get(emptyGuid);
            // If it's still missing for some reason, make it
            if (!store) {
                store = this.newEmptyChat();
            }
        }
        // We know at this point no matter what we have a store
        return store;
    }

    @action
    setCurrentChatId(chatId: string) {
        this.currentChatId = chatId;
    }

    // Sets the current chat from a chat session pulled from the backend
    @action
    setCurrentChatFromChatSession(chatSession: ChatSession) {
        this.currentChatId = chatSession.id;
        // If we don't have a store for this chat id, make it
        if (!this.allChats.has(chatSession.id)) {
            this.allChats.set(
                chatSession.id,
                new SingleAgentChatStore({
                    chat: Chat.fromChatSession(chatSession),
                    rootStore: this.rootStore,
                    agentStore: this,
                }),
            );
        } else if (!this.allChats.get(chatSession.id)!.chatLoaded) {
            // If we DO have a store for this chat id and it has NOT been loaded yet, update it
            this.allChats
                .get(chatSession.id)!
                .updateChat(Chat.fromChatSession(chatSession));
        }
    }

    // Set the store for a given chatId
    @action
    setChat(newKey: string, chatStore: SingleAgentChatStore) {
        this.allChats.set(newKey, chatStore);
    }

    // Creates a new empty chat store, deleting the old one if it exists
    // In most cases the old one has been turned into the store for a new chat
    @action
    resetEmptyChat() {
        this.allChats.delete(emptyGuid);
        this.newEmptyChat();
    }

    // Reset the empty chat then set our chatId to the empty one
    @action
    newChat() {
        this.currentChatStore.abort();
        this.resetEmptyChat();
        this.currentChatId = emptyGuid;
        this.isEmpty = true;
    }

    // Global agent state functions
    @action
    setFullscreen(fullscreen: boolean) {
        this.fullscreen = fullscreen;
    }

    @action
    setRouteBeforeFullscreen(route: string) {
        this.routeBeforeFullscreen = route;
    }

    @action
    setIsEmpty(isEmpty: boolean) {
        this.isEmpty = isEmpty;
    }

    @action
    setLoadingCurrentChat(loading: boolean) {
        this.loadingCurrentChat = loading;
    }

    @action
    setVisibleChatIds(chatIds: string[]) {
        this.visibleChatIds = chatIds;
    }

    // Set the chatStore from a chat object with no messages
    @action
    addChat(chat: ChatNoMessages) {
        this.allChats.set(
            chat.id,
            new SingleAgentChatStore({
                chat: Chat.fromChatNoMessage(chat),
                rootStore: this.rootStore,
                agentStore: this,
            }),
        );
    }

    // Remove the chat store for the associated chat
    // Also remove it in visibleChatIds if its there
    @action
    removeChat(chat: ChatNoMessages) {
        this.allChats.delete(chat.id);
        if (this.visibleChatIds.includes(chat.id)) {
            this.visibleChatIds = this.visibleChatIds.filter(
                (id) => id !== chat.id,
            );
        }
    }

    public hasChat(chatId: string): boolean {
        return this.allChats.has(chatId);
    }

    // This computes the categories used in the sidebar of KBo Fullscreen
    @computed
    get categorizedChats() {
        return categorizeChats(
            Array.from(this.allChats.values()).filter(
                (chatStore) => chatStore.chat.id !== emptyGuid,
            ),
            this.visibleChatIds,
        );
    }

    // Async task to request access to KBo, sending the email etc.
    @action public requestAccess() {
        this.setupAsyncTask(AgentStore.Tasks.RequestAccess, async () => {
            try {
                await this.service.requestAccessToKbo();
                this.isAccessRequested = true;
            } catch (error) {
                if (error instanceof ServiceError) {
                    this.messageStore.logError(
                        error.errorMessage ?? error.message,
                    );

                    return;
                }
                this.messageStore.logError("An unknown error occurred.");
            }
        });
    }

    // Drawer functions
    @action
    setIsDrawerOpen(open: boolean) {
        this.isDrawerOpen = open;
    }

    // Closing KBo only hides it; an in-flight request keeps running so its
    // response is there when the chat is reopened.
    @action closeDrawer() {
        this.setIsDrawerOpen(false);
    }

    @action openDrawer() {
        this.markKboUpdateSeen();
        this.setIsDrawerOpen(true);
    }

    @action initializeKboUpdateAnnouncement() {
        if (this.kboUpdateAnnouncementActive !== undefined) return;

        const userId = this.authStore.effectiveUserId;
        if (!userId) return;

        this.kboUpdateAnnouncementActive = !hasSeenKboUpdate(userId);
    }

    @action markKboUpdateSeen() {
        this.initializeKboUpdateAnnouncement();

        if (KBO_UPDATE_ALWAYS_VISIBLE) return;
        if (this.kboUpdateSparkleCleared) return;
        this.kboUpdateSparkleCleared = true;

        const userId = this.authStore.effectiveUserId;
        if (!userId) return;

        storeKboUpdateSeen(userId);
    }

    @computed get showKboUpdateAnnouncement(): boolean {
        return this.kboUpdateAnnouncementActive === true;
    }

    @computed get showKboUpdateSparkle(): boolean {
        return this.showKboUpdateAnnouncement && !this.kboUpdateSparkleCleared;
    }

    @action toggleDrawer() {
        if (this.isDrawerOpen) {
            this.closeDrawer();
        } else {
            this.openDrawer();
        }
    }
}
